<template>
  <base-modal-component>
    <template v-slot:header>
      <div class="c-seo-modal__heading">
        <h4 class="u-typography-color md-title">
          {{ update === 'Create' ? 'Create SEO rule' : 'Edit SEO rule' }}
        </h4>
        <div class="c-seo-modal__context">
          <span class="c-seo-modal__chip">
            <app-icon
              name="public"
              :size="14"
            />
            {{ website.name }}
          </span>
          <span class="c-seo-modal__chip">
            <app-icon
              name="translate"
              :size="14"
            />
            {{ language.name }}
          </span>
          <span class="c-seo-modal__chip">
            <span class="c-seo-modal__chip-label">{{ pageType }}</span>
            {{ pageInfo }}
          </span>
        </div>
      </div>
    </template>

    <template v-slot:body>
      <div
        v-if="error && error.messages"
        class="ui-alert ui-alert--danger c-seo-modal__alert"
        role="alert"
      >
        <app-icon name="error" />
        <span>{{ error.messages }}</span>
      </div>

      <!-- meta tags -->
      <section class="c-seo-modal__section">
        <h5 class="c-seo-modal__section-title">
          Meta tags
        </h5>


        <label class="c-seo-modal__field">
          <span class="c-seo-modal__label">
            Meta title
            <span :class="['c-seo-modal__counter', { 'is-over': length(singleSeoRule.metaTitle) > limits.metaTitle }]">
              {{ length(singleSeoRule.metaTitle) }} / {{ limits.metaTitle }}
            </span>
          </span>
          <input
            v-model="singleSeoRule.metaTitle"
            class="ui-field__input"
            type="text"
            name="metaTitle"
            autocomplete="off"
            placeholder="Shown as the title in search results"
            :disabled="!canEdit"
          >
        </label>

        <label class="c-seo-modal__field">
          <span class="c-seo-modal__label">
            Meta description
            <span :class="['c-seo-modal__counter', { 'is-over': length(singleSeoRule.metaDescription) > limits.metaDescription }]">
              {{ length(singleSeoRule.metaDescription) }} / {{ limits.metaDescription }}
            </span>
          </span>
          <textarea
            v-model="singleSeoRule.metaDescription"
            class="ui-field__input c-seo-modal__textarea"
            name="metaDescription"
            rows="3"
            placeholder="Short summary shown under the title in search results"
            :disabled="!canEdit"
          />
        </label>

        <div class="c-seo-modal__grid">
          <label class="c-seo-modal__field">
            <span class="c-seo-modal__label">Meta keywords</span>
            <input
              v-model="singleSeoRule.metaKeywords"
              class="ui-field__input"
              type="text"
              name="metaKeywords"
              autocomplete="off"
              placeholder="Comma separated"
              :disabled="!canEdit"
            >
          </label>

          <label class="c-seo-modal__field">
            <span class="c-seo-modal__label">Page title (H1)</span>
            <input
              v-model="singleSeoRule.pageTitle"
              class="ui-field__input"
              type="text"
              name="pageTitle"
              autocomplete="off"
              placeholder="Main heading on the page"
              :disabled="!canEdit"
            >
          </label>
        </div>

        <div class="c-seo-modal__noindex">
          <md-switch
            v-model="singleSeoRule.noindex"
            :disabled="!canEdit"
          >
            Hide from search engines (noindex)
          </md-switch>
          <p>When on, search engines are asked not to index this page.</p>
        </div>
      </section>

      <!-- content -->
      <section class="c-seo-modal__section">
        <h5 class="c-seo-modal__section-title">
          Page content
        </h5>
        <wysiwyg-component
          v-model="singleSeoRule.content"
          :disabled="!canEdit"
        />
      </section>
    </template>

    <template v-slot:footer>
      <generic-button
        variation="grey"
        @click="close"
      >
        Close
      </generic-button>
      <generic-button
        variation="red"
        :disabled="!canEdit"
        @click="submit"
      >
        {{ update }}
      </generic-button>
    </template>
  </base-modal-component>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { seoRulesService } from '@/services/seo-rules'
import { seoRulesTargetTypeMapper } from '@/const/seo-rules'
import GenericButton from '@/shared/buttons/GenericButton'
import BaseModalComponent from '@/shared/modal/BaseModalComponent'
import { responseService } from '@/services/response'
import WysiwygComponent from '@/shared/wysiwyg-component/WysiwygComponent';
import {onlinePermissionsMixin} from "@/mixins/onlinePermissionsMixin";
import {PERMISSION_NAMES} from "@/const/online-permissions";
import AppIcon from '@/shared/app-icon/AppIcon';

export default {
  name: 'EditSeoRuleModal',
  components: {
    WysiwygComponent,
    GenericButton,
    BaseModalComponent,
    AppIcon,
  },

  mixins: [onlinePermissionsMixin],

  props: {
    seoRule: {
      type: Object,
      required: true,
    },
    website: {
      type: Object,
      required: true
    },
    language: {
      type: Object,
      required: true
    },
    handleSuccessCallback: {
      type: Function,
      required: false,
      default: () => {}
    }
  },

  data() {
    return {
      singleSeoRule: {
        content: '',
        metaDescription: '',
        metaKeywords: '',
        metaTitle: '',
        pageTitle: '',
        noindex: false,
      },
      messages: {
        error: null,
        success: null,
      },
      // common search-result display limits
      limits: {
        metaTitle: 60,
        metaDescription: 160,
      },
    };
  },

  computed: {
    PERMISSION_NAMES() {
      return PERMISSION_NAMES
    },
    ...mapGetters("response", ["success", "error"]),

    canEdit() {
      return this.canWrite(`${PERMISSION_NAMES.WEB_SITES.key}.${PERMISSION_NAMES.SEO_RULES.key}`);
    },
    update() {
      return !this.singleSeoRule.id ? 'Create' : 'Update';
    },

    pageType() {
      return seoRulesTargetTypeMapper[this.seoRule.entityType];
    },

    pageInfo() {
      switch (this.seoRule.entityType) {
        case 'RegionEntity':
          return `${ this.seoRule.target.sport.name } - ${ this.seoRule.translation }`;
        case 'LeagueEntity':
          return `${ this.seoRule.target.region.sport.name } - ${ this.seoRule.target.region.name } - ${ this.seoRule.translation }`;
        case 'CasinoCategoryEntity':
          return `${ this.seoRule.translation }`;
        default:
          return `${ this.seoRule.translation }`;
      }
    },
  },

  created() {
    this.getSeoRule();
  },

  methods: {
    ...mapActions("response", ["handleSuccess", "handleError", "resetError"]),
    length(value) {
      return String(value || '').length;
    },

    async getSeoRule() {
      const { entityType, entityId } = this.seoRule;

      try {
        const singleSeoRuleData = await seoRulesService.getSingleSeoRule(this.website.id, this.language.id, entityType, entityId)
        this.singleSeoRule = singleSeoRuleData?.data
      } catch (err) {
        // If entity is not found we will be creating new one, no additional checks needed
      }
    },

    async handleCreateSeoRule() {
      const createData = {
        ...this.singleSeoRule,
        websiteId: this.website.id,
        onlineLanguageId: this.language.id,
        target: {
          id: this.seoRule.entityId,
          type: seoRulesTargetTypeMapper[this.seoRule.entityType]
        }
      }

      try {
        await seoRulesService.create(createData);

        this.handleSuccess({ message: 'Successfully updated SEO rule' })
        this.close();
      } catch (err) {
        this.handleError(responseService.getErrorMessage(err.data.message));
      }
    },

    async handleUpdateSeoRule() {
      try {
        await seoRulesService.update(this.singleSeoRule.id, {
          content: this.singleSeoRule.content,
          metaDescription: this.singleSeoRule.metaDescription,
          metaKeywords: this.singleSeoRule.metaKeywords,
          metaTitle: this.singleSeoRule.metaTitle,
          pageTitle: this.singleSeoRule.pageTitle,
          noindex: this.singleSeoRule.noindex,
        });

        this.handleSuccess({ message: 'Successfully updated SEO rule' })
        this.close();
      } catch (err) {
        this.handleError(responseService.getErrorMessage(err.data.message));
      }
    },

    async submit() {
      !this.singleSeoRule.id ? await this.handleCreateSeoRule() : await this.handleUpdateSeoRule();
      this.handleSuccessCallback();
    },

    close() {
      this.resetError();
      this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
.u-capitalize {
  text-transform: capitalize;
}

// ---------- header ----------
.c-seo-modal__heading {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.c-seo-modal__context {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.c-seo-modal__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  height: 24px;
  padding: 0 10px 0 8px;
  overflow: hidden;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-full);
  background-color: var(--bg-subtle);
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;

  .c-app-icon {
    color: var(--text-muted);
  }
}

.c-seo-modal__chip-label {
  color: var(--text-muted);
  text-transform: capitalize;

  &:after {
    content: ':';
  }
}

// ---------- body ----------
.c-seo-modal__alert {
  margin-bottom: 16px;
}

.c-seo-modal__section {
  display: flex;
  flex-direction: column;
  gap: 16px;

  & + & {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid var(--border-default);
  }
}

.c-seo-modal__section-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.c-seo-modal__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

.c-seo-modal__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;

  .ui-field__input {
    height: 40px;
    font-size: 14px;
  }

  &.is-invalid .ui-field__input {
    border-color: var(--danger-fg);
  }
}

.c-seo-modal__textarea.ui-field__input {
  height: auto;
  min-height: 84px;
  padding: 10px 12px;
  line-height: 1.5;
  resize: vertical;
}

.c-seo-modal__label {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
}

.c-seo-modal__counter {
  font-size: 12px;
  font-weight: 400;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;

  &.is-over {
    color: var(--warning-fg);
    font-weight: 600;
  }
}

.c-seo-modal__error {
  font-size: 12px;
  color: var(--danger-fg);
}

.c-seo-modal__noindex {
  padding: 12px 16px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background-color: var(--bg-subtle);

  .md-switch {
    margin: 0;
  }

  p {
    margin: 4px 0 0 46px;
    font-size: 12px;
    color: var(--text-muted);
  }
}
</style>
